import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Lock,
  ShieldCheck,
  Stethoscope,
  Loader2,
  Tag,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { useClinicFormat } from '@/context/ClinicTimezoneContext'
import { useConsultations } from '@/hooks/useConsultations'
import { VITALS, toNumericVital, vitalUnit } from '@/lib/vitals'
import type { ConsultationSummary } from '@/services/consultation.service'
import { EvolutionStatusBadge } from './EvolutionStatus'
import { ClarifyingNotesSection } from './ClarifyingNotesSection'

const VISIT_TYPE_LABELS: Record<string, string> = {
  routine_control: 'Control rutinario',
  urgent: 'Urgente',
  follow_up: 'Seguimiento',
  first_visit: 'Primera consulta',
  other: 'Otro',
}

const PROGRESS_LABELS: Record<string, string> = {
  improved: 'Mejoró',
  unchanged: 'Sin cambios',
  worsened: 'Empeoró',
  new_condition: 'Condición nueva',
}

const ADHERENCE_LABELS: Record<string, string> = {
  full: 'Apego completo',
  partial: 'Apego parcial',
  none: 'No tomó tratamiento',
  not_applicable: 'No aplica',
}

const ADJUSTMENT_LABELS: Record<string, string> = {
  no_changes: 'Sin cambios',
  new_medication: 'Medicamento nuevo',
  discontinued: 'Medicamento suspendido',
  dosage_change: 'Cambio de dosis',
}

interface ProgressNoteCardProps {
  note: ConsultationSummary
  /** Chronological visit number (1 = first visit). */
  visitNumber: number
  isAuthor?: boolean
  isAdmin?: boolean
  defaultOpen?: boolean
}

/**
 * One progress note (nota de evolución) as an expandable/collapsible card.
 *
 * Collapsed: header only (visit #, date, physician, reason, evolution + lock
 * badges). Expanded: lazy-loads the full SOAP note, vitals, assessment, plan,
 * prognosis, attestation + integrity hash, and the append-only clarifying notes.
 * Signed notes are read-only; corrections are addenda, never edits.
 */
export function ProgressNoteCard({
  note,
  visitNumber,
  isAuthor = false,
  isAdmin = false,
  defaultOpen = false,
}: ProgressNoteCardProps) {
  const { formatDateTime } = useClinicFormat()
  const [open, setOpen] = useState(defaultOpen)

  // Lazy-load the full note only when expanded (the summary lacks S/O/plan).
  const { useConsultation } = useConsultations()
  const { data: full, isLoading } = useConsultation(open ? note.id : '')

  const isLocked = note.status === 'locked' || note.status === 'unlock_requested'
  const dt = note.completed_at ?? note.created_at

  const vitalsEntries = VITALS.map((v) => ({
    def: v,
    value: toNumericVital((full?.vitals ?? note.vitals)?.[v.key]),
  })).filter((e) => e.value != null)

  return (
    <div className="overflow-hidden rounded-2xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest shadow-sm">
      {/* ── Header (always visible, click to expand) ──────────────────── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-ml-surface-container-low/60"
        aria-expanded={open}
      >
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-ml-primary/10 text-ml-primary">
          <Stethoscope className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-ml-primary">Visita #{visitNumber}</span>
            <span className="text-xs text-ml-on-surface-variant/70">{formatDateTime(dt)}</span>
            {note.visit_type && (
              <span className="inline-flex items-center gap-1 rounded bg-ml-primary/10 px-2 py-0.5 text-[10px] font-semibold text-ml-primary">
                <Tag className="h-2.5 w-2.5" />
                {VISIT_TYPE_LABELS[note.visit_type] ?? note.visit_type}
              </span>
            )}
            <EvolutionStatusBadge status={note.evolution_status} />
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                isLocked
                  ? 'bg-rose-100 text-rose-800'
                  : 'bg-emerald-100 text-emerald-800',
              )}
            >
              <Lock className="h-3 w-3" />
              {isLocked ? 'Bloqueada' : 'Firmada'}
            </span>
          </div>
          <p className="mt-1 truncate text-sm text-ml-on-surface">
            {note.chief_complaint || 'Sin motivo registrado'}
          </p>
          <p className="mt-0.5 text-[11px] text-ml-on-surface-variant/70">
            {note.attested_by_name || 'Médico'}
            {note.attested_by_cedula && <> · Céd. prof. {note.attested_by_cedula}</>}
          </p>
        </div>
        {open ? (
          <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-ml-on-surface-variant/60" />
        ) : (
          <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-ml-on-surface-variant/60" />
        )}
      </button>

      {/* ── Body (lazy) ───────────────────────────────────────────────── */}
      {open && (
        <div className="border-t border-ml-outline-variant/15 px-5 py-5">
          {isLoading || !full ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-ml-primary" />
            </div>
          ) : (
            <div className="space-y-5">
              <Section label="Subjetivo · lo que refiere el paciente">
                <Text value={full.subjective_notes} />
              </Section>

              <Section label="Objetivo · hallazgos del médico">
                {vitalsEntries.length > 0 && (
                  <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                    {vitalsEntries.map(({ def, value }) => (
                      <div
                        key={def.key}
                        className="rounded-md bg-ml-surface-container-high/50 p-2 text-center"
                      >
                        <p className="text-[10px] text-ml-on-surface-variant/70">{def.label}</p>
                        <p className="text-sm font-bold text-ml-primary">
                          {value} <span className="text-[10px] font-normal">{vitalUnit(def.key)}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <Text value={full.objective_notes} />
              </Section>

              <Section label="Valoración · diagnóstico y comparación">
                {((full.diagnosis_codes ?? []).length > 0 || full.evolution_status) && (
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    {(full.diagnosis_codes ?? []).map((code) => (
                      <span
                        key={code}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200/70 bg-emerald-50/70 px-2.5 py-1 font-mono text-xs font-bold text-emerald-800"
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                        {code}
                      </span>
                    ))}
                    <EvolutionStatusBadge status={full.evolution_status} />
                  </div>
                )}
                <Text value={full.assessment} />
              </Section>

              <Section label="Plan · tratamiento, estudios y seguimiento">
                <Text value={full.plan_notes} />
              </Section>

              {full.prognosis && (
                <Section label="Pronóstico">
                  <Text value={full.prognosis} />
                </Section>
              )}

              {/* Extended structured fields (standalone note metadata) */}
              {full.progress_note_ext && (() => {
                const ext = full.progress_note_ext
                const chips: string[] = []
                if (ext.progress_since_last_visit) chips.push(PROGRESS_LABELS[ext.progress_since_last_visit] ?? ext.progress_since_last_visit)
                if (ext.treatment_adherence) chips.push(ADHERENCE_LABELS[ext.treatment_adherence] ?? ext.treatment_adherence)
                if (ext.treatment_adjustment) {
                  const adj = ADJUSTMENT_LABELS[ext.treatment_adjustment] ?? ext.treatment_adjustment
                  chips.push(ext.treatment_adjustment_detail ? `${adj}: ${ext.treatment_adjustment_detail}` : adj)
                }
                if (ext.studies_ordered?.length) chips.push(`Estudios: ${ext.studies_ordered.join(', ')}`)
                if (ext.next_appointment_date) chips.push(`Próxima cita: ${ext.next_appointment_date}`)
                if (chips.length === 0) return null
                return (
                  <Section label="Datos adicionales de la visita">
                    <div className="flex flex-wrap gap-1.5">
                      {chips.map((c, i) => (
                        <span
                          key={i}
                          className="rounded-md bg-ml-surface-container px-2 py-0.5 text-xs text-ml-on-surface"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  </Section>
                )
              })()}

              {/* Attestation + integrity hash */}
              {full.attested_by_name && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-xs text-emerald-900">
                  <div className="flex items-center gap-1.5 font-semibold">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Firmada por {full.attested_by_name}
                    {full.attested_by_cedula && <> · Céd. prof. {full.attested_by_cedula}</>}
                    {full.attested_at && <> · {formatDateTime(full.attested_at)}</>}
                  </div>
                  {full.content_hash && (
                    <p className="mt-1 break-all font-mono text-[10px] text-emerald-700/80">
                      SHA-256: {full.content_hash}
                    </p>
                  )}
                </div>
              )}

              {/* Append-only clarifying notes (addenda) */}
              <ClarifyingNotesSection consultationId={note.id} canAdd={isAuthor || isAdmin} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-ml-primary">
        {label}
      </h4>
      {children}
    </div>
  )
}

function Text({ value }: { value?: string | null }) {
  if (!value || !value.trim()) {
    return <p className="text-xs italic text-ml-on-surface-variant/50">Sin información registrada.</p>
  }
  return <p className="whitespace-pre-wrap text-sm leading-relaxed text-ml-on-surface">{value}</p>
}
